import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { generalExtraActions } from 'reduxStore/actions';

const INITIAL_STATE = {
  sidebarOpen: true, // open by default on large screens; closed on small (set on mount)
  users: [],
  usersLoading: false,
  usersError: null,
  usersPagination: {
    count: 0,
    next: null,
    previous: null,
  },
  cities: [],
  citiesLoading: false,
  citiesError: null,
  subRoles: [],
  subRolesLoading: false,
  subRolesError: null,
  subRolesPagination: {
    count: 0,
    next: null,
    previous: null,
  },
  subRolesByRole: {},
  subRolesByRoleLoading: false,
  subRolesByRoleError: null,
  grounds: [],
  groundsLoading: false,
  groundsError: null,
  groundsPagination: {
    count: 0,
    next: null,
    previous: null,
  },
  fields: [],
  fieldsLoading: false,
  fieldsError: null,
  fieldsPagination: {
    count: 0,
    next: null,
    previous: null,
  },
  sports: [],
  sportsLoading: false,
  sportsError: null,
  amenities: [],
  amenitiesLoading: false,
  amenitiesError: null,
  bookings: [],
  bookingsLoading: false,
  bookingsError: null,
  bookingsPagination: {
    count: 0,
    next: null,
    previous: null,
  },
  reports: [],
  reportsLoading: false,
  reportsError: null,
  reportsPagination: {
    count: 0,
    next: null,
    previous: null,
  },
  notifications: [],
  notificationsLoading: false,
  notificationsError: null,
  notificationsPagination: {
    count: 0,
    next: null,
    previous: null,
  },
  unreadNotificationCount: 0,
  reportDetail: null,
  reportDetailLoading: false,
  reportDetailError: null,
};

export const generalSlice = createSlice({
  name: 'general',
  initialState: INITIAL_STATE,
  reducers: {
    resetGeneralSlice: () => INITIAL_STATE,
    resetData: (state, {payload}) => {
      state.transcriptionData = state.originalTranscriptionData = state.summaryData = {};
    },
    setSidebarOpen: (state, {payload}) => {
      state.sidebarOpen = payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
  },
  extraReducers: (builder) => {
    // Users actions
    builder.addCase(
      generalExtraActions.getUsers.fulfilled,
      (state, {payload}) => {
        state.usersLoading = false;
        state.users = payload.results || [];
        state.usersPagination = {
          count: payload.count || 0,
          next: payload.next,
          previous: payload.previous,
        };
        state.usersError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getUsers.pending,
      (state) => {
        state.usersLoading = true;
        state.usersError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getUsers.rejected,
      (state, {payload}) => {
        state.usersLoading = false;
        state.usersError = payload;
      }
    );

    // Cities actions
    builder.addCase(
      generalExtraActions.getCities.fulfilled,
      (state, {payload}) => {
        state.citiesLoading = false;
        state.cities = payload || [];
        state.citiesError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getCities.pending,
      (state) => {
        state.citiesLoading = true;
        state.citiesError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getCities.rejected,
      (state, {payload}) => {
        state.citiesLoading = false;
        state.citiesError = payload;
      }
    );

    // SubRoles actions
    builder.addCase(
      generalExtraActions.getSubRoles.fulfilled,
      (state, {payload}) => {
        state.subRolesLoading = false;
        state.subRoles = payload.results || [];
        state.subRolesPagination = {
          count: payload.count || 0,
          next: payload.next,
          previous: payload.previous,
        };
        state.subRolesError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getSubRoles.pending,
      (state) => {
        state.subRolesLoading = true;
        state.subRolesError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getSubRoles.rejected,
      (state, {payload}) => {
        state.subRolesLoading = false;
        state.subRolesError = payload;
      }
    );

    // SubRoles by Role actions
    builder.addCase(
      generalExtraActions.getSubRolesByRole.fulfilled,
      (state, {payload}) => {
        state.subRolesByRoleLoading = false;
        // Handle the new API response structure
        if (payload.admin) {
          state.subRolesByRole.admin = payload.admin || [];
        }
        if (payload.ground_admin) {
          state.subRolesByRole.ground_admin = payload.ground_admin || [];
        }
        state.subRolesByRoleError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getSubRolesByRole.pending,
      (state) => {
        state.subRolesByRoleLoading = true;
        state.subRolesByRoleError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getSubRolesByRole.rejected,
      (state, {payload}) => {
        state.subRolesByRoleLoading = false;
        state.subRolesByRoleError = payload;
      }
    );

    // Grounds actions
    builder.addCase(
      generalExtraActions.getGrounds.fulfilled,
      (state, {payload}) => {
        state.groundsLoading = false;
        state.grounds = payload.results || [];
        state.groundsPagination = {
          count: payload.count || 0,
          next: payload.next,
          previous: payload.previous,
        };
        state.groundsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getGrounds.pending,
      (state) => {
        state.groundsLoading = true;
        state.groundsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getGrounds.rejected,
      (state, {payload}) => {
        state.groundsLoading = false;
        state.groundsError = payload;
      }
    );

    // Slot actoins
    builder.addCase(
      generalExtraActions.getFields.fulfilled,
      (state, {payload}) => {
        state.fieldsLoading = false;
        state.fields = payload.results || [];
        state.fieldsPagination = {
          count: payload.count || 0,
          next: payload.next,
          previous: payload.previous,
        };
        state.fieldsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getFields.pending,
      (state) => {
        state.fieldsLoading = true;
        state.fieldsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getFields.rejected,
      (state, {payload}) => {
        state.fieldsLoading = false;
        state.fieldsError = payload;
      }
    );

    // Sports actions
    builder.addCase(
      generalExtraActions.getSports.fulfilled,
      (state, {payload}) => {
        state.sportsLoading = false;
        state.sports = payload || [];
        state.sportsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getSports.pending,
      (state) => {
        state.sportsLoading = true;
        state.sportsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getSports.rejected,
      (state, {payload}) => {
        state.sportsLoading = false;
        state.sportsError = payload;
      }
    );

    // Amenities actions
    builder.addCase(
      generalExtraActions.getAmenities.fulfilled,
      (state, {payload}) => {
        state.amenitiesLoading = false;
        state.amenities = payload || [];
        state.amenitiesError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getAmenities.pending,
      (state) => {
        state.amenitiesLoading = true;
        state.amenitiesError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getAmenities.rejected,
      (state, {payload}) => {
        state.amenitiesLoading = false;
        state.amenitiesError = payload;
      }
    );

    // Bookings actions
    builder.addCase(
      generalExtraActions.getBookings.fulfilled,
      (state, {payload}) => {
        state.bookingsLoading = false;
        state.bookings = payload.results || [];
        state.bookingsPagination = {
          count: payload.count || 0,
          next: payload.next,
          previous: payload.previous,
        };
        state.bookingsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getBookings.pending,
      (state) => {
        state.bookingsLoading = true;
        state.bookingsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getBookings.rejected,
      (state, {payload}) => {
        state.bookingsLoading = false;
        state.bookingsError = payload;
      }
    );

    // Reports actions
    builder.addCase(
      generalExtraActions.getReports.fulfilled,
      (state, {payload}) => {
        state.reportsLoading = false;
        state.reports = payload.results || [];
        state.reportsPagination = {
          count: payload.count || 0,
          next: payload.next,
          previous: payload.previous,
        };
        state.reportsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getReports.pending,
      (state) => {
        state.reportsLoading = true;
        state.reportsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getReports.rejected,
      (state, {payload}) => {
        state.reportsLoading = false;
        state.reportsError = payload;
      }
    );

    // Report detail actions
    builder.addCase(
      generalExtraActions.getReportDetail.fulfilled,
      (state, {payload}) => {
        state.reportDetailLoading = false;
        state.reportDetail = payload;
        state.reportDetailError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getReportDetail.pending,
      (state) => {
        state.reportDetailLoading = true;
        state.reportDetailError = null;
      }
    );
    builder.addCase(
      generalExtraActions.getReportDetail.rejected,
      (state, {payload}) => {
        state.reportDetailLoading = false;
        state.reportDetailError = payload;
      }
    );

    // Notifications actions (must be before addMatcher)
    builder.addCase(
      generalExtraActions.fetchNotifications.fulfilled,
      (state, {payload}) => {
        state.notificationsLoading = false;
        state.notifications = payload.results || [];
        state.notificationsPagination = {
          count: payload.count || 0,
          next: payload.next,
          previous: payload.previous,
        };
        state.notificationsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.fetchNotifications.pending,
      (state) => {
        state.notificationsLoading = true;
        state.notificationsError = null;
      }
    );
    builder.addCase(
      generalExtraActions.fetchNotifications.rejected,
      (state, {payload}) => {
        state.notificationsLoading = false;
        state.notificationsError = payload;
      }
    );

    // Unread notification count
    builder.addCase(
      generalExtraActions.getUnreadNotificationCount.fulfilled,
      (state, {payload}) => {
        state.unreadNotificationCount = payload.count || 0;
      }
    );

    // Report action handlers (resolve, dismiss, under review)
    builder.addMatcher(
      isAnyOf(
        generalExtraActions.resolveReport.fulfilled,
        generalExtraActions.dismissReport.fulfilled,
        generalExtraActions.markReportUnderReview.fulfilled
      ),
      (state, {payload}) => {
        // Update the report in the reports list if it exists
        const reportIndex = state.reports.findIndex(report => report.id === payload.id);
        if (reportIndex !== -1) {
          state.reports[reportIndex] = payload;
        }
        // Update report detail if it's the same report
        if (state.reportDetail && state.reportDetail.id === payload.id) {
          state.reportDetail = payload;
        }
      }
    );
  },
});

export const {actions: generalActions, reducer} = generalSlice;
export default reducer;
